import { Badge, Card, Flex, Text } from "@radix-ui/themes";
import { CalendarClock } from "lucide-react";

interface Props {
  status: string;
  openAt: string | null;
  closeAt: string | null;
}

/** `Event#applications_status`, which is finer than open/closed. */
const LABELS: Record<
  string,
  { text: string; color: "green" | "amber" | "gray" | "red" }
> = {
  open: { text: "Open", color: "green" },
  not_open_yet: { text: "Not open yet", color: "amber" },
  closed: { text: "Closed", color: "gray" },
  // No window was ever set, so nobody can apply. It reads as "Closed" everywhere
  // else in the app, which hides a misconfiguration behind a normal-looking state.
  not_configured: { text: "No window set", color: "red" },
};

export default function ApplicationWindowCard({
  status,
  openAt,
  closeAt,
}: Props) {
  const label = LABELS[status] ?? LABELS.closed;

  return (
    <Card>
      <Flex direction="column" gap="2">
        <Flex align="center" justify="between" gap="2">
          <Flex align="center" gap="1">
            <CalendarClock size={14} />
            <Text size="2" weight="medium">
              Applications
            </Text>
          </Flex>
          <Badge variant="soft" color={label.color}>
            {label.text}
          </Badge>
        </Flex>

        {status === "not_configured" ? (
          <Text size="1" color="gray">
            Set an opening date on the event to start taking applications.
          </Text>
        ) : (
          <Flex direction="column" gap="1">
            <Flex gap="2">
              <Text size="1" color="gray" style={{ minWidth: 48 }}>
                Opens
              </Text>
              <Text size="1">{openAt ?? "—"}</Text>
            </Flex>
            <Flex gap="2">
              <Text size="1" color="gray" style={{ minWidth: 48 }}>
                Closes
              </Text>
              {/* No closing date means the window runs until the event itself. */}
              <Text size="1">{closeAt ?? "Day of the event"}</Text>
            </Flex>
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
